<template>
  <form @submit.prevent="onSubmit">
    <div>
      <template v-if="isExternalDriver" />
      <template v-else-if="booking.customer">
        <div v-if="!isSwappingCustomer" class="grid gap-y-2 md:flex md:justify-between">
          <LabeledText :label="t('customerAccount')">
            {{ booking.customer?.email }}
          </LabeledText>
          <CVButton
            v-if="$can('update', subject('Booking', booking), 'customer')"
            outline
            @click.prevent="isSwappingCustomer = true"
            >{{ t('swapAccount') }}</CVButton
          >
        </div>
        <div v-else>
          <SwapCustomer
            :schema="customerSchema"
            :is-saving="isSaving"
            @swap-customer="swapCustomer"
            @cancel="isSwappingCustomer = false"
          />
        </div>
      </template>
      <p v-else class="text-muted-foreground text-sm">
        {{ t('customerAccountCreatedOnHandover') }}
      </p>
      <Divider v-if="!isExternalDriver" class="col-span-3" />
    </div>

    <div class="relative">
      <CustomerForm
        ref="customerFormComp"
        :booking="booking"
        :contact-email-optional="contactEmailOptional"
        :read-only-contact="readOnlyContact"
        @has-changed="(changed) => (hasChanged.customerData = changed)"
        @scan-documents="scanningDriverNo = 0"
      />
      <div v-if="isSwappingCustomer" class="bg-accent/70 absolute inset-0 z-10"></div>
    </div>

    <Divider class="col-span-3" />

    <AdditionalDrivers
      v-if="!isLongTerm"
      ref="additionalDriversComp"
      :booking="booking"
      validation-is-mandatory
      show-license-scan
      handover-adjustment
      :price-difference-counterparty="CounterpartyType.CUSTOMER"
      @has-changed="(changed) => (hasChanged.additionalDrivers = changed)"
      @scan-documents="(index) => (scanningDriverNo = index)"
    />

    <Divider class="col-span-3" />

    <div
      v-if="hasMoreYoungDriversThenBooked"
      class="flex items-center gap-2 rounded-md bg-white p-4"
    >
      <CircleAlertIcon class="size-4" />{{
        isLongTerm
          ? t('longTermYoungDriverBlocked')
          : t('needTooChangeYoungDriversMessage', {
              count: numYoungDrivers! - numBookedYoungDrivers,
            })
      }}
    </div>

    <div class="mt-5 flex items-center justify-end gap-4">
      <span v-if="waitingForCustomer">
        {{ t('waitingForCustomer') }}
      </span>
      <ButtonNext
        :is-loading="isSaving"
        :disabled="unsaved || waitingForCustomer || (isLongTerm && !!hasMoreYoungDriversThenBooked)"
        type="submit"
      >
        {{ t('next') }}
      </ButtonNext>
    </div>
  </form>
  <Modal
    v-if="isDocumentScanningEnabled"
    :key="scanningDriverNo"
    :model-value="scanningDriverNo !== undefined"
    is-full-screen
    @update:model-value="finishScanning()"
  >
    <HandoverDocumentScan
      :driver-subject="scanningDriverNo ?? 0"
      :require-id="scanningDriverNo === 0"
      :require-license="true"
      @close="finishScanning"
      @scanned="extract"
    />
  </Modal>
  <Modal
    v-if="isDocumentScanningEnabled"
    :model-value="scanningDriverNo === undefined && runningExtractions > 0"
    @update:model-value.stop
  >
    <HandoverDocumentScanLoader />
  </Modal>
</template>

<script lang="ts" setup>
import AdditionalDrivers from '@/components/booking-forms/AdditionalDrivers.vue';
import ButtonNext from '@/components/buttons/ButtonNext.vue';
import Divider from '@/components/Divider.vue';
import type { Booking } from '@/entities/bookings/booking.entity';
import { usePreventLeavingUnsavedForm } from '@/hooks/use-prevent-leaving-unsaved-form';
import { useI18n } from 'vue-i18n';
import { computed, reactive, ref } from 'vue';
import SwapCustomer from '@/components/booking-forms/customer/SwapCustomer.vue';
import { customerEditSchema } from '@/validation/customer.schema';
import { useUpdateBooking } from '@/queries/use-bookings';
import CustomerForm from './CustomerForm.vue';
import { subject } from '@casl/ability';
import type { User } from '@/entities/auth/user.entity';
import { useGenerateSwapCustomerDto } from '@/components/booking-forms/customer/use-generate-swap-customer-dto';
import LabeledText from '@/components/LabeledText.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import { useYoungDrivers } from '@/views/handover/components/client-data/use-young-drivers.ts';
import { CircleAlertIcon } from 'lucide-vue-next';
import Modal from '@/components/Modal.vue';
import HandoverDocumentScan from './HandoverDocumentScan.vue';
import type { DocumentDataExtractionResult } from '@/api/users.service';
import { usersService } from '@/api/users.service';
import HandoverDocumentScanLoader from './HandoverDocumentScanLoader.vue';
import type { CustomerDataDto } from '@/entities/bookings/customer-data.dto';
import { Local } from '@/entities/auth/local.enum';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { provideAiValidation } from '@/hooks/use-ai-validation.ts';
import { Alert } from '@/services/alert.service';
import { CounterpartyType } from '@carvia/ros-client-types';
import { fromCalendarDate } from '@/utils/calendar-date';
import { isLongTermPricing } from '@/utils/pricing.utils';
import { useExternalDriver } from '@/hooks/use-external-driver';

const props = defineProps<{
  booking: Booking;
  contactEmailOptional?: boolean;
  readOnlyContact?: boolean;
  waitingForCustomer?: boolean;
}>();

const emit = defineEmits<{
  (e: 'onSubmit'): void;
}>();

const { t } = useI18n();

const isDocumentScanningEnabled = useFeatureFlag('documents-scanning');
const { isExternalDriver } = useExternalDriver();

const isLongTerm = computed(() => isLongTermPricing(props.booking.priceCalculation));

const customerFormComp = ref<InstanceType<typeof CustomerForm>>();
const additionalDriversComp = ref<InstanceType<typeof AdditionalDrivers>>();

const hasChanged = reactive({
  customerData: false,
  customerDocuments: false,
  additionalDrivers: false,
});

const isSwappingCustomer = ref(false);

const { mutateAsync: updateBooking, isPending: isSaving } = useUpdateBooking();

const needsScanningDrivers = computed(() => {
  return Array.from({ length: (props.booking.additionalDrivers.length ?? 0) + 1 }, (_, i) => i);
});
const scanningDriverNo = ref<number | undefined>(
  needsScanningDrivers.value.find((i) => !window.history.state[`scanningFinished-${i}`]),
);

function finishScanning() {
  window.history.state[`scanningFinished-${scanningDriverNo.value}`] = true;
  scanningDriverNo.value = needsScanningDrivers.value.find(
    (i) => !window.history.state[`scanningFinished-${i}`],
  );
}

function mergeAiValidations(validations: DocumentDataExtractionResult, additionalDriver?: number) {
  for (let i = aiValidation.length - 1; i >= 0; i--) {
    const validation = aiValidation[i];
    if (
      (!additionalDriver && !validation.field.startsWith('additionalDrivers')) ||
      (additionalDriver &&
        validation.field.startsWith(`additionalDrivers[${additionalDriver - 1}]`))
    ) {
      aiValidation.splice(i, 1);
    }
  }
  for (const validation of validations) {
    aiValidation.push({
      ...validation,
      field: !additionalDriver
        ? validation.field
        : `additionalDrivers[${additionalDriver - 1}].${validation.field}`,
    });
  }
}

function createCustomerData(data: Partial<User> | null): CustomerDataDto {
  return {
    firstName: data?.firstName ?? undefined,
    lastName: data?.lastName ?? undefined,
    dateOfBirth: data?.dateOfBirth ?? undefined,
    street: data?.street ?? undefined,
    zip: data?.zip ?? undefined,
    city: data?.city ?? undefined,
    country: data?.country ?? undefined,
    phone: data?.phone ?? undefined,
    vatId: data?.vatId ?? undefined,
    idCardNumber: data?.idCardNumber ?? undefined,
    idCardDate: data?.idCardDate ?? undefined,
    licenseNumber: data?.licenseNumber ?? undefined,
    licenseDate: data?.licenseDate ?? undefined,
    licenseAuthority: data?.licenseAuthority ?? undefined,
    idCardAuthority: data?.idCardAuthority ?? undefined,
    preferredLocal: data?.preferredLocal ?? Local.EN_US,
  };
}

const aiValidation = reactive<DocumentDataExtractionResult>([]);
provideAiValidation(aiValidation);

const runningExtractions = ref(0);

async function extract(files: File[]) {
  const driverNo = scanningDriverNo.value!;
  runningExtractions.value++;
  finishScanning();

  try {
    const customerData =
      driverNo === 0
        ? createCustomerData(props.booking.customerData ?? null)
        : createCustomerData(props.booking.additionalDrivers[driverNo - 1] ?? null);

    const result = await usersService.extractDocuments(
      files,
      customerData,
      props.booking.bookingNumber.toString(),
      driverNo !== 0, // onlyLicenseData is true for additional drivers (driverNo > 0)
    );
    mergeAiValidations(result, driverNo);
    if (driverNo === 0) {
      customerFormComp.value?.setValues({
        idCardNumber: result.find((f) => f.field === 'idCardNumber')?.validation.value ?? undefined,
        idCardAuthority:
          result.find((f) => f.field === 'idCardAuthority')?.validation.value ?? undefined,
        idCardDate: toDate(
          result.find((f) => f.field === 'idCardDate')?.validation.value ?? undefined,
        ),
        licenseNumber:
          result.find((f) => f.field === 'licenseNumber')?.validation.value ?? undefined,
        licenseAuthority:
          result.find((f) => f.field === 'licenseAuthority')?.validation.value ?? undefined,
        licenseDate: toDate(
          result.find((f) => f.field === 'licenseDate')?.validation.value ?? undefined,
        ),
      });
    } else {
      // TODO Remove as any when fixed https://github.com/logaretm/vee-validate/issues/5074
      additionalDriversComp.value?.setFieldValue(
        `additionalDrivers[${driverNo - 1}].licenseNumber` as any,
        result.find((f) => f.field === 'licenseNumber')?.validation.value ?? undefined,
      );
      additionalDriversComp.value?.setFieldValue(
        `additionalDrivers[${driverNo - 1}].licenseAuthority` as any,
        result.find((f) => f.field === 'licenseAuthority')?.validation.value ?? undefined,
      );
      additionalDriversComp.value?.setFieldValue(
        `additionalDrivers[${driverNo - 1}].licenseDate` as any,
        toDate(result.find((f) => f.field === 'licenseDate')?.validation.value ?? undefined),
      );
    }
  } catch (error) {
    console.error(error);
  } finally {
    runningExtractions.value--;
  }
}

function toDate(v: string | undefined): Date | undefined {
  return v ? fromCalendarDate(v) : undefined;
}

const {
  numBookedYoungDrivers,
  numYoungDrivers,
  hasMoreYoungDriversThenBooked,
  updateYoungDriverExtra,
} = useYoungDrivers(computed(() => props.booking));

const customerSchema = customerEditSchema(
  computed(() => props.booking.bookedCar.vehicleType.minAge),
  computed(() => props.booking.startDate),
);

const generateSwapCustomerDto = useGenerateSwapCustomerDto();
const swapCustomer = async (customer: User) => {
  await updateBooking({
    id: props.booking.id,
    booking: await generateSwapCustomerDto(customer),
  });
  isSwappingCustomer.value = false;
};

const onSubmit = async () => {
  if (!customerFormComp.value) return;
  if (!isLongTerm.value && !additionalDriversComp.value) return;

  const customerDataValidation = await customerFormComp.value.validate();
  const additionalDriversValidation = isLongTerm.value
    ? { valid: true }
    : await additionalDriversComp.value!.validate();
  if (!customerDataValidation.valid || !additionalDriversValidation.valid) return;

  if (hasMoreYoungDriversThenBooked.value) {
    if (isLongTerm.value) {
      return;
    }
    const hasBeenUpdated = await updateYoungDriverExtra();
    if (!hasBeenUpdated) return;
  }

  const idFields = ['idCardNumber', 'idCardAuthority', 'idCardDate'] as const;
  const licenseFields = ['licenseNumber', 'licenseAuthority', 'licenseDate'] as const;

  const emptyDocs = [];
  if (idFields.every((p) => !props.booking.customerData?.[p])) {
    emptyDocs.push(`${t('idCard')} ${t('customer')}`);
  }

  if (licenseFields.every((p) => !props.booking.customerData?.[p])) {
    emptyDocs.push(`${t('license')} ${t('customer')}`);
  }

  for (let i = 0; i < props.booking.additionalDrivers.length; i++) {
    if (licenseFields.every((p) => !props.booking.additionalDrivers[i]?.[p])) {
      emptyDocs.push(`${t('license')} ${t('additionalDriver', { index: i + 1 })}`);
    }
  }

  if (emptyDocs.length > 0) {
    const confirmed = await Alert.fire({
      title: t('missingDocuments'),
      html: [t('missingDocumentsMessage'), ...emptyDocs].join('<br>'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('yes'),
      cancelButtonText: t('no'),
    });
    if (!confirmed.isConfirmed) return;
  }

  if (props.waitingForCustomer) return;

  emit('onSubmit');
};

const unsaved = computed(() => Object.values(hasChanged).some((changed) => changed));

usePreventLeavingUnsavedForm(() => unsaved.value);
</script>

<i18n lang="json">
{
  "en": {
    "swapAccount": "Swap Account",
    "back": "Back to Booking Overview",
    "customerAccount": "Linked customer account",
    "customerAccountCreatedOnHandover": "A customer account will be created when the handover is completed.",
    "needTooChangeYoungDriversMessage": "There are additional costs for {count} young drivers that have not been considered in the booking so far!",
    "longTermYoungDriverBlocked": "The main renter is under the young-driver age limit, and this extra is not booked. Handover cannot be completed until the quote includes the young-driver extra.",
    "scanDocuments": "Scan Documents",
    "customer": "Customer",
    "additionalDriver": "Additional Driver {index}",
    "idCard": "ID Card",
    "license": "License",
    "missingDocuments": "Missing Documents",
    "missingDocumentsMessage": "For the following documents, no data could be extracted. Continue anyway?",
    "waitingForCustomer": "Waiting for the customer to confirm contact details on the counter screen…"
  },
  "de": {
    "swapAccount": "Konto austauschen",
    "back": "Zurück zur Buchungsübersicht",
    "customerAccount": "Verknüpftes Kundenkonto",
    "customerAccountCreatedOnHandover": "Bei Abschluss der Übergabe wird ein Kundenkonto erstellt.",
    "needTooChangeYoungDriversMessage": "Es fallen zusätzliche Kosten für {count} Jungfahrer an, die bisher in der Buchung nicht berücksichtigt wurden!",
    "longTermYoungDriverBlocked": "Der Hauptmieter liegt unter der Jungfahrer-Altersgrenze, und das Extra ist nicht gebucht. Die Übergabe kann erst abgeschlossen werden, wenn das Jungfahrer-Extra im Angebot enthalten ist.",
    "scanDocuments": "Dokumente scannen",
    "customer": "Kunde",
    "additionalDriver": "Zusätzlicher Fahrer {index}",
    "idCard": "Personalausweis",
    "license": "Führerschein",
    "missingDocuments": "Fehlende Dokumente",
    "missingDocumentsMessage": "Für die folgenden Dokumente konnte keine Daten extrahiert werden. Trotzdem fortfahren?",
    "waitingForCustomer": "Warten auf die Bestätigung der Kontaktdaten durch den Kunden am Schalter…"
  },
  "es": {
    "swapAccount": "Cambiar cuenta",
    "back": "Volver a la vista general de la reserva",
    "customerAccount": "Cuenta de cliente vinculada",
    "customerAccountCreatedOnHandover": "Se creará una cuenta de cliente al completar la entrega.",
    "needTooChangeYoungDriversMessage": "Hay costes adicionales por {count} conductores jóvenes que aún no están reflejados en la reserva.",
    "longTermYoungDriverBlocked": "El conductor principal no alcanza la edad mínima y el extra de conductor joven no está reservado. No se puede completar la entrega hasta que el extra esté en la oferta.",
    "scanDocuments": "Escanear documentos",
    "customer": "Cliente",
    "additionalDriver": "Conductor adicional {index}",
    "idCard": "Documento de identidad",
    "license": "Permiso de conducir",
    "missingDocuments": "Documentos faltantes",
    "missingDocumentsMessage": "No se pudieron extraer datos de los siguientes documentos. ¿Continuar de todos modos?",
    "waitingForCustomer": "Esperando a que el cliente confirme los datos de contacto en el mostrador…"
  }
}
</i18n>
